import React, { memo, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import DefaultView from "../components/DefaultView";
import PaymentForm from "../components/PaymentForm";
import CancelSubscriptionModal from "../components/CancelSubscriptionModal";
import ReactivateSubscriptionModal from "../components/ReactivateSubscriptionModal";
import ManagePaymentMethodsModal from "../components/ManagePaymentMethodsModal";
import ClaimFreeNumberModal from "../components/ClaimFreeNumberModal";
import { actionGetSubscriptionPlans, actionCreateStripeCustomer, actionCreateSubscription } from "../store/actions/subscriptionAction";
import { actionGetFaxNumberSubscriptions } from "../store/actions/faxSubscriptionAction";

const Subscription = memo(
  ({
    user,
    actionGetSubscriptionPlans,
    actionCreateStripeCustomer,
    actionCreateSubscription,
    actionGetFaxNumberSubscriptions,
    subscriptionPlans,
    subscriptionPlansLoader,
    subscriptionCustomerLoader,
    subscriptionCreateLoader,
    stripeCustomer,
  }) => {
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showReactivateModal, setShowReactivateModal] = useState(false);
    const [showPaymentMethodsModal, setShowPaymentMethodsModal] = useState(false);

    // Free-number claim (after Starter purchase) + fax numbers to display
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [claimCustomerId, setClaimCustomerId] = useState(null);
    const [claimPaymentMethodId, setClaimPaymentMethodId] = useState(null);
    const [faxNumbers, setFaxNumbers] = useState([]);
    const [faxNumbersLoading, setFaxNumbersLoading] = useState(true);

    const loadFaxNumbers = async () => {
      setFaxNumbersLoading(true);
      try {
        const data = await actionGetFaxNumberSubscriptions();
        setFaxNumbers(Array.isArray(data) ? data : []);
      } catch {
        setFaxNumbers([]);
      } finally {
        setFaxNumbersLoading(false);
      }
    };

    // Only the free (Starter-included) number is surfaced on this page
    const freeFaxNumbers = faxNumbers.filter((s) => s.is_free);

    // Plan features mapping based on plan code
    const planFeaturesMap = {
      starter: [
        "150 Pages",
        "ONE Free Number",
        "Additional Numbers @6.99 USD/month",
        "OCR Search Enabled",
        "API Enabled",
      ],
      business: [
        "1000 Pages",
        "Numbers - Pay as you go, @4.99 USD/month",
        "OCR Search Enabled",
        "API Enabled",
      ],
    };

    // Monthly prices mapping based on plan code
    const planPricesMap = {
      starter: 14.99,
      business: 29.99,
    };

    useEffect(() => {
      actionGetSubscriptionPlans();
      loadFaxNumbers();
    }, [actionGetSubscriptionPlans]);

    const currentPlan = user?.main_subscription?.plan?.code || null;
    const hasActiveSubscription = user?.main_subscription && user.main_subscription.status === "active" && !user.main_subscription.cancel_at_period_end;
    const hasCanceledSubscription = user?.main_subscription && (
      user.main_subscription.status === "canceled" ||
      user.main_subscription.cancel_at_period_end === true ||
      user.main_subscription.cancel_at_period_end === 1 ||
      user.main_subscription.cancel_at
    );

    // Debug logging - remove in production
    console.log("Subscription Debug:", {
      main_subscription: user?.main_subscription,
      hasActiveSubscription,
      hasCanceledSubscription,
      cancel_at_period_end: user?.main_subscription?.cancel_at_period_end,
      cancel_at: user?.main_subscription?.cancel_at,
      status: user?.main_subscription?.status
    });

    const handleSelectPlan = (planCode) => {
      setSelectedPlan(planCode);
      setShowPaymentModal(true);
      setPaymentError(null);
    };

    const handleSubscriptionCheckout = async () => {
      if (!stripe || !elements) {
        setPaymentError("Stripe has not loaded yet. Please try again.");
        return;
      }

      setIsProcessing(true);
      setPaymentError(null);

      try {
        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
          throw new Error("Card element not found");
        }

        // Step 1: Get or create Stripe customer — prefer stored ID over session
        let customerId = user?.stripe_customer_id || stripeCustomer?.id;

        if (!customerId) {
          const customer = await actionCreateStripeCustomer({
            name: user.user_fullname,
            email: user.user_email,
          });
          customerId = customer.id;
        }

        // Step 2: Find the plan ID from subscriptionPlans
        const selectedPlanObj = subscriptionPlans.find(p => p.code === selectedPlan);
        if (!selectedPlanObj) {
          throw new Error("Selected plan not found");
        }

        // Step 3: Create payment method
        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: user.user_fullname,
            email: user.user_email,
          },
        });

        if (pmError) {
          throw new Error(pmError.message || "Failed to create payment method");
        }

        // Step 4: Create subscription
        const subscriptionResponse = await actionCreateSubscription({
          customerId: customerId,
          planId: selectedPlanObj.id.toString(),
          paymentMethodId: paymentMethod.id,
        });

        // Step 5: Extract client secret from response
        const clientSecret = subscriptionResponse.clientSecret;

        // If no client secret, payment was already processed or subscription is already active
        if (!clientSecret) {
          // Wait for webhook to update subscription status
          setPaymentError("Processing your subscription...");
          await new Promise(resolve => setTimeout(resolve, 3000));
          setPaymentError(null);

          // Show success
          setSubscriptionData(subscriptionResponse);
          setSuccessMessage(
            `✓ Subscription activated successfully! You are now on the ${selectedPlanObj.name}.`
          );
          setSubscriptionSuccess(true);
          setShowPaymentModal(false);
          if (selectedPlan === "starter") {
            setClaimCustomerId(customerId);
            setClaimPaymentMethodId(paymentMethod.id);
            setShowClaimModal(true);
          }
          setSelectedPlan(null);
          cardElement.clear();
          return;
        }

        // Step 6: Confirm card payment with Stripe
        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: user.user_fullname,
                email: user.user_email,
              },
            },
          }
        );

        if (confirmError) {
          throw new Error(confirmError.message || "Payment confirmation failed");
        }

        // Step 7: Handle payment intent status
        switch (paymentIntent?.status) {
          case "succeeded":
            // Payment successful - wait briefly for webhook to process
            setPaymentError(null);

            // Show processing message
            setPaymentError("Payment successful! Activating your subscription...");

            // Wait for webhook to update subscription status (2-3 seconds)
            await new Promise(resolve => setTimeout(resolve, 3000));

            // Clear the processing message
            setPaymentError(null);

            // Show success
            setSubscriptionData(subscriptionResponse);
            setSuccessMessage(
              `✓ Subscription activated successfully! You are now on the ${selectedPlanObj.name}.`
            );
            setSubscriptionSuccess(true);
            setShowPaymentModal(false);
            if (selectedPlan === "starter") {
              setClaimCustomerId(customerId);
              setClaimPaymentMethodId(paymentMethod.id);
              setShowClaimModal(true);
            }
            setSelectedPlan(null);
            cardElement.clear();

            // Refresh user data to get updated subscription
            // You might want to call a Redux action here to refresh user data
            // For example: dispatch(actionRefreshUserData());
            break;

          case "requires_action":
            // 3D Secure authentication required
            throw new Error(
              "Additional authentication is required. Please complete the verification process."
            );

          case "requires_payment_method":
            // Payment failed - need new payment method
            throw new Error(
              "Payment failed. Please check your card details and try again."
            );

          case "processing":
            // Payment is being processed
            setPaymentError(
              "Your payment is being processed. This may take a few moments. Please check your subscription status shortly."
            );
            setShowPaymentModal(false);
            setSelectedPlan(null);
            break;

          default:
            throw new Error(`Unexpected payment status: ${paymentIntent?.status}`);
        }
      } catch (error) {
        console.error("Subscription checkout error:", error);
        setPaymentError(error.message || "Failed to process subscription. Please try again.");
      } finally {
        setIsProcessing(false);
      }
    };

    return (
      <div className="page-content">
        <div className="container-fluid">
          {/* start page title */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 main_heading">Subscription Plans</h4>
                <div className="page-title-right">
                  <DefaultView />
                </div>
              </div>
              <p className="text-center mt-2 text-muted small">
                * All prices are in USD
              </p>
            </div>
          </div>
          {/* end page title */}

          {/* Success Message */}
          {subscriptionSuccess && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <div className="d-flex align-items-start">
                    <div className="flex-shrink-0">
                      <i className="fas fa-check-circle fs-3 text-success me-3"></i>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="alert-heading fw-bold mb-2">
                        <i className="fas fa-check-circle me-2"></i>
                        Payment Successful!
                      </h5>
                      <p className="mb-2">{successMessage}</p>
                      {subscriptionData && (
                        <div className="small">
                          <p className="mb-1">
                            <strong>Subscription ID:</strong> {subscriptionData.id}
                          </p>
                          <p className="mb-1">
                            <strong>Billing Period:</strong>{" "}
                            {new Date(subscriptionData.current_period_start * 1000).toLocaleDateString()} -{" "}
                            {new Date(subscriptionData.current_period_end * 1000).toLocaleDateString()}
                          </p>
                          <p className="mb-0">
                            <strong>Next Billing Date:</strong>{" "}
                            {new Date(subscriptionData.current_period_end * 1000).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      <div className="mt-3">
                        <button
                          className="btn btn-sm btn-success me-2"
                          onClick={() => window.location.reload()}
                        >
                          <i className="fas fa-sync-alt me-1"></i>
                          Refresh Page
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => navigate("/dashboard")}
                        >
                          <i className="fas fa-home me-1"></i>
                          Go to Dashboard
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSubscriptionSuccess(false)}
                      aria-label="Close"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading state for subscription data */}
          {faxNumbersLoading && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-body p-4">
                    <div className="skeleton mb-3" style={{ height: 26, width: 240, borderRadius: 6 }} />
                    <div className="skeleton mb-2" style={{ height: 16, width: 180, borderRadius: 6 }} />
                    <div className="skeleton mb-2" style={{ height: 16, width: 150, borderRadius: 6 }} />
                    <div className="skeleton" style={{ height: 16, width: 200, borderRadius: 6 }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Current Plan Status - Show for any subscription */}
          {!faxNumbersLoading && user?.main_subscription && (
            <div className="row mb-3">
              <div className="col-12">
                <div
                  className={`card shadow-sm ${hasActiveSubscription ? 'border-primary' : 'border-warning'}`}
                  style={{
                    background: hasActiveSubscription
                      ? "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)"
                      : "linear-gradient(135deg, #FFF8E1 0%, #FFF3C4 100%)",
                  }}
                >
                  <div className="card-body p-4">
                    {/* Free (Starter-included) fax number — shown on top */}
                    {freeFaxNumbers.length > 0 && (
                      <div className="mb-3 p-3 rounded" style={{ background: "rgba(52, 195, 143, 0.10)", border: "1px solid #b7edd3" }}>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          <i className="fas fa-fax text-success"></i>
                          <span className="fw-semibold me-1">Your Free Fax Number{freeFaxNumbers.length > 1 ? "s" : ""}:</span>
                          {freeFaxNumbers.map((sub) => (
                            <span key={sub.id || sub.stripe_subscription_id} className="badge bg-white text-dark border d-inline-flex align-items-center" style={{ fontSize: 13, padding: "6px 10px" }}>
                              {sub.faxNumber?.number ?? "—"}
                              <span className="badge bg-success ms-2">FREE</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Debug info - remove in production */}
                    {process.env.NODE_ENV === 'development' && (
                      <div className="alert alert-info small mb-3">
                        <strong>Debug:</strong> Status: {user.main_subscription.status},
                        Cancel at period end: {String(user.main_subscription.cancel_at_period_end)},
                        Cancel at: {user.main_subscription.cancel_at || 'null'}
                      </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <div>
                        <h5 className="fw-bold mb-2">
                          Current Plan:{" "}
                          <span className={hasActiveSubscription ? "text-primary" : "text-warning"}>
                            {user.main_subscription.plan?.name || "Subscription Plan"}
                          </span>
                        </h5>
                        <p className="text-muted mb-1 small">
                          Status: <span className={`fw-semibold text-uppercase ${hasActiveSubscription ? 'text-success' : 'text-warning'}`}>
                            {hasCanceledSubscription && user.main_subscription.cancel_at_period_end
                              ? "Canceling at period end"
                              : user.main_subscription.status || "Active"}
                          </span>
                        </p>
                        <p className="text-muted mb-1 small">
                          Billing: <span className="fw-semibold">Monthly</span>
                        </p>
                        {user?.main_subscription?.current_period_end && (
                          <p className="text-muted mb-0 small">
                            {hasCanceledSubscription ? "Access until" : "Next billing date"}:{" "}
                            <span className="fw-semibold">
                              {new Date(
                                user.main_subscription.current_period_end
                              ).toLocaleDateString()}
                            </span>
                          </p>
                        )}
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {hasActiveSubscription && currentPlan === "starter" && faxNumbers.length === 0 && (
                          <button
                            onClick={() => {
                              setClaimCustomerId(
                                user?.stripe_customer_id ||
                                  user?.main_subscription?.stripe_customer_id ||
                                  null
                              );
                              setClaimPaymentMethodId(null);
                              setShowClaimModal(true);
                            }}
                            className="btn btn-success fw-semibold shadow-sm"
                          >
                            <i className="fas fa-gift me-1"></i>
                            Claim Your Free Number
                          </button>
                        )}
                        <button
                          onClick={() => navigate("/billing/history")}
                          className="btn btn-outline-secondary fw-semibold shadow-sm"
                        >
                          View Billing History
                        </button>
                        <button
                          onClick={() => setShowPaymentMethodsModal(true)}
                          className="btn btn-outline-info fw-semibold shadow-sm"
                        >
                          <i className="fas fa-credit-card me-1"></i>
                          Manage Payment Methods
                        </button>
                        {hasActiveSubscription && (
                          <button
                            onClick={() => setShowCancelModal(true)}
                            className="btn btn-outline-danger fw-semibold shadow-sm"
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel Plan
                          </button>
                        )}
                        {hasCanceledSubscription && (
                          <button
                            onClick={() => setShowReactivateModal(true)}
                            className="btn btn-success fw-semibold shadow-sm"
                          >
                            <i className="fas fa-undo-alt me-1"></i>
                            Reactivate Plan
                          </button>
                        )}
                      </div>
                    </div>
                    {hasCanceledSubscription && (
                      <div className="mt-3 p-3 bg-warning bg-opacity-10 rounded">
                        <div className="d-flex align-items-start">
                          <i className="fas fa-exclamation-triangle text-warning me-2 mt-1"></i>
                          <div className="small">
                            <strong>Subscription Canceled:</strong> Your subscription will remain active until{" "}
                            {user.main_subscription.current_period_end
                              ? new Date(user.main_subscription.current_period_end).toLocaleDateString()
                              : "the end of your billing period"}.
                            You can reactivate it anytime before it expires.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {subscriptionPlansLoader && (
            <div className="row g-3 mb-4 justify-content-center">
              {[1, 2].map((i) => (
                <div key={i} className="col-12 col-md-6 col-lg-5">
                  <div
                    className="card h-100 shadow"
                    style={{ borderRadius: "16px" }}
                  >
                    <div className="card-body p-4">
                      <div
                        className="skeleton mb-3"
                        style={{
                          height: "30px",
                          width: "60%",
                          margin: "0 auto",
                        }}
                      />
                      <div
                        className="skeleton mb-4"
                        style={{
                          height: "60px",
                          width: "50%",
                          margin: "0 auto",
                        }}
                      />
                      {[1, 2, 3, 4, 5].map((j) => (
                        <div
                          key={j}
                          className="skeleton mb-2"
                          style={{ height: "20px", width: "100%" }}
                        />
                      ))}
                      <div
                        className="skeleton mt-4"
                        style={{ height: "45px", width: "100%" }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Plans Grid */}
          {!subscriptionPlansLoader && subscriptionPlans.length > 0 && (
            <div className="row g-3 mb-4 justify-content-center">
              {subscriptionPlans.filter(p => ["starter", "business"].includes(p.code)).map((plan) => {
                const isCurrentPlan = currentPlan === plan.code;
                const isPopular = plan.code === "starter";
                const features = planFeaturesMap[plan.code] || [];
                const monthlyPrice = planPricesMap[plan.code] || 0;

                return (
                  <div
                    key={plan.id}
                    className="col-12 col-md-6 col-lg-5"
                    style={{ marginTop: isCurrentPlan ? "16px" : "0" }}
                  >
                    <div
                      className={`card h-100 shadow position-relative ${isPopular ? "border-primary border-3" : "border-2"
                        }`}
                      style={{
                        borderRadius: "16px",
                        overflow: "visible",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {/* Popular Badge */}
                      {isPopular && (
                        <div
                          className="position-absolute top-0 start-50 translate-middle"
                          style={{ zIndex: 15 }}
                        >
                          <span
                            className="badge bg-primary text-white fw-bold px-4 py-2 rounded-pill shadow text-uppercase"
                            style={{
                              letterSpacing: "1px",
                              fontSize: "0.7rem",
                            }}
                          >
                            <span className="me-1">⭐</span>
                            Most Popular
                          </span>
                        </div>
                      )}

                      {/* Current Plan Badge */}
                      {isCurrentPlan && (
                        <div
                          className="position-absolute top-0 end-0 translate-middle-y me-3"
                          style={{
                            zIndex: 20,
                            marginTop: isPopular ? "4px" : "2px",
                          }}
                        >
                          <span
                            className="badge bg-success text-white fw-bold px-3 py-2 rounded-pill shadow text-uppercase"
                            style={{
                              letterSpacing: "0.5px",
                              fontSize: "0.7rem",
                            }}
                          >
                            ✓ Active
                          </span>
                        </div>
                      )}

                      <div className="card-body p-4">
                        {/* Plan Name */}
                        <div className="text-center mb-2">
                          <h3
                            className="fw-bold mb-2"
                            style={{ letterSpacing: "-0.5px" }}
                          >
                            {plan.name}
                          </h3>
                          {plan.description && (
                            <p className="text-muted small mb-0">
                              {plan.description}
                            </p>
                          )}
                        </div>

                        {/* Pricing */}
                        <div className="text-center py-3 mb-4 border-bottom">
                          <div className="d-flex align-items-start justify-content-center mb-1">
                            <span className="h4 fw-bold text-primary mt-2">
                              $
                            </span>
                            <span
                              className="display-3 fw-bolder lh-1"
                              style={{ letterSpacing: "-2px" }}
                            >
                              {monthlyPrice.toString().split(".")[0]}
                            </span>
                            <span className="h4 fw-bold text-muted mt-2">
                              .{monthlyPrice.toString().split(".")[1] || "00"}
                            </span>
                          </div>
                          <p
                            className="text-uppercase fw-semibold text-muted mb-0 small"
                            style={{ letterSpacing: "1px" }}
                          >
                            Per Month
                          </p>
                        </div>

                        {/* Features List */}
                        <div className="mb-4">
                          {features.map((feature, idx) => (
                            <div
                              key={idx}
                              className="d-flex align-items-start gap-3 mb-2 py-1"
                            >
                              <div
                                className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0 mt-1"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                }}
                              >
                                <svg
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                  }}
                                  className="text-primary"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                              <span className="small fw-medium lh-base">
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* CTA Button */}
                        <button
                          onClick={() => handleSelectPlan(plan.code)}
                          disabled={isCurrentPlan || subscriptionCreateLoader}
                          className={`btn w-100 fw-bold text-uppercase shadow ${isCurrentPlan ? "btn-secondary" : "btn-primary"
                            }`}
                          style={{
                            padding: "14px 20px",
                            borderRadius: "10px",
                            letterSpacing: "0.5px",
                            fontSize: "0.9375rem",
                          }}
                        >
                          {isCurrentPlan
                            ? "✓ Current Plan"
                            : `Get ${plan.name.split(" ")[0]}`}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* FAQ Section */}
          <div className="row mt-5">
            <div className="col-12">
              <div className="card shadow-sm border">
                <div className="card-body p-4">
                  <h4 className="fw-bold mb-4">
                    Frequently Asked Questions
                  </h4>
                  <div className="d-flex flex-column gap-3">
                    <div>
                      <h5 className="fw-semibold mb-2 small">
                        Can I change my plan later?
                      </h5>
                      <p
                        className="text-muted mb-0"
                        style={{
                          fontSize: "0.8125rem",
                          lineHeight: "1.5",
                        }}
                      >
                        Yes, you can upgrade or downgrade your plan at any
                        time. Changes will be reflected in your next billing
                        cycle.
                      </p>
                    </div>
                    <div>
                      <h5 className="fw-semibold mb-2 small">
                        What happens if I exceed my fax limit?
                      </h5>
                      <p
                        className="text-muted mb-0"
                        style={{
                          fontSize: "0.8125rem",
                          lineHeight: "1.5",
                        }}
                      >
                        Once you reach your monthly page limit, sending is
                        paused until your plan renews. Upgrade your plan any
                        time for a higher page allowance.
                      </p>
                    </div>
                    <div>
                      <h5 className="fw-semibold mb-2 small">
                        Can I cancel my subscription?
                      </h5>
                      <p
                        className="text-muted mb-0"
                        style={{
                          fontSize: "0.8125rem",
                          lineHeight: "1.5",
                        }}
                      >
                        Yes, you can cancel anytime. Your subscription will
                        remain active until the end of your current billing
                        period.
                      </p>
                    </div>
                    <div>
                      <h5 className="fw-semibold mb-2 small">
                        Do you offer refunds?
                      </h5>
                      <p
                        className="text-muted mb-0"
                        style={{
                          fontSize: "0.8125rem",
                          lineHeight: "1.5",
                        }}
                      >
                        We offer a 30-day money-back guarantee for first-time
                        subscribers if you're not satisfied with our service.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        {showPaymentModal && (
          <div
            className="modal d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-credit-card me-2"></i>
                    Complete Your Subscription
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowPaymentModal(false);
                      setPaymentError(null);
                    }}
                    disabled={isProcessing}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  {paymentError && (
                    <div className="alert alert-danger d-flex align-items-start" role="alert">
                      <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
                      <div>{paymentError}</div>
                    </div>
                  )}
                  <PaymentForm
                    onSubmit={handleSubscriptionCheckout}
                    isLoading={isProcessing || subscriptionCustomerLoader || subscriptionCreateLoader}
                    planAmount={planPricesMap[selectedPlan]}
                    selectedPlanName={
                      subscriptionPlans.find(p => p.code === selectedPlan)?.name || "Selected Plan"
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Subscription Modal */}
        <CancelSubscriptionModal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          subscription={{
            ...user?.main_subscription,
            id: user?.main_subscription?.stripe_subscription_id
          }}
        />

        {/* Reactivate Subscription Modal */}
        <ReactivateSubscriptionModal
          show={showReactivateModal}
          onHide={() => setShowReactivateModal(false)}
          subscription={{
            ...user?.main_subscription,
            id: user?.main_subscription?.stripe_subscription_id
          }}
        />

        {/* Manage Payment Methods Modal */}
        <ManagePaymentMethodsModal
          show={showPaymentMethodsModal}
          onHide={() => setShowPaymentMethodsModal(false)}
          customerId={user?.stripe_customer_id || user?.main_subscription?.stripe_customer_id}
        />

        {/* Claim Free Number Modal (after Starter purchase) */}
        <ClaimFreeNumberModal
          show={showClaimModal}
          onClose={() => setShowClaimModal(false)}
          customerId={claimCustomerId}
          paymentMethodId={claimPaymentMethodId}
          userId={user?.user_ID}
          onClaimed={() => loadFaxNumbers()}
        />
      </div>
    );
  }
);

const mapStateToProps = (state) => {
  return {
    user: state.auth.user,
    subscriptionPlans: state.subscription.subscriptionPlans,
    subscriptionPlansLoader: state.subscription.subscriptionPlansLoader,
    subscriptionCustomerLoader: state.subscription.subscriptionCustomerLoader,
    subscriptionCreateLoader: state.subscription.subscriptionCreateLoader,
    stripeCustomer: state.subscription.stripeCustomer,
  };
};

export default connect(mapStateToProps, {
  actionGetSubscriptionPlans,
  actionCreateStripeCustomer,
  actionCreateSubscription,
  actionGetFaxNumberSubscriptions,
})(Subscription);
